import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Trash2 } from 'lucide-react';
import { assessmentService, companyService, anonymousAuditService, claimService, paymentService } from '../services';
import TemplatePicker from '../components/TemplatePicker';
import CompanyOnboardingForm from '../components/CompanyOnboardingForm';
import Layout from '../components/Layout';
import DeleteAuditModal from '../components/DeleteAuditModal';
import CategoryCard from '../components/CategoryCard';
import MeasuresTeaser from '../components/MeasuresTeaser';
import { Card, Badge, ScoreRing, Button, Dialog, Toast, OverflowMenu } from '../components/ds';
import { useActiveAudit } from '../contexts/ActiveAuditContext';
import { getRisk, fetchPosture } from '../utils/posture';
import { useIsMobile } from '../hooks/useMediaQuery';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const BRAND = '#1E4FBE';

// ── Short radar labels for the DIN SPEC 27076 category names ───────────
const SHORT = {
  'Organisation & Sensibilisierung':          'Organisation',
  'Identitäts- und Berechtigungsmanagement':  'Berechtigungen',
  'Datensicherung':                           'Datensicherung',
  'Patch- und Änderungsmanagement':           'Patch-Management',
  'Schutz vor Schadprogrammen':               'Schadprogramme',
  'IT-Systeme und Netzwerke':                 'IT-Systeme',
};

// ── Radar chart (pure SVG) ─────────────────────────────────────────────
const RadarChart = ({ data }) => {
  const isMobile = useIsMobile();
  const SIZE = isMobile ? 220 : 300;
  const cx = SIZE / 2, cy = SIZE / 2;
  const R  = SIZE * 0.29;
  const n  = data.length;
  if (n < 3) return null;

  const ang = (i) => (i * 2 * Math.PI / n) - Math.PI / 2;
  const pt  = (i, r) => ({ x: cx + r * Math.cos(ang(i)), y: cy + r * Math.sin(ang(i)) });

  const gridPolys = [0.25, 0.5, 0.75, 1].map(lv =>
    data.map((_, i) => { const p = pt(i, R * lv); return `${p.x},${p.y}`; }).join(' ')
  );
  const dataPoly = data.map((d, i) => { const p = pt(i, R * (Math.max(d.score, 3) / 100)); return `${p.x},${p.y}`; }).join(' ');

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width="100%" style={{ maxWidth: SIZE, overflow: 'visible' }}>
      {/* Grid polygons */}
      {gridPolys.map((pts, i) => (
        <polygon key={i} points={pts} fill="none" stroke="#D7DCE4" strokeWidth="1" />
      ))}
      {/* Axes */}
      {data.map((_, i) => {
        const p = pt(i, R);
        return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#D7DCE4" strokeWidth="1" />;
      })}
      {/* Data fill */}
      <polygon points={dataPoly} fill="rgba(30,79,190,0.12)" stroke={BRAND} strokeWidth="2" strokeLinejoin="round" />
      {/* Data dots */}
      {data.map((d, i) => {
        const p = pt(i, R * (Math.max(d.score, 3) / 100));
        return <circle key={i} cx={p.x} cy={p.y} r={4} fill={BRAND} stroke="#fff" strokeWidth="2" />;
      })}
      {/* Labels */}
      {data.map((d, i) => {
        const a  = ang(i);
        const lp = pt(i, R + 34);
        const anchor = Math.cos(a) > 0.15 ? 'start' : Math.cos(a) < -0.15 ? 'end' : 'middle';
        const dy1    = Math.sin(a) > 0.1 ? 14 : -4;
        return (
          <g key={i}>
            <text x={lp.x} y={lp.y + dy1}    textAnchor={anchor} fontSize="10.5" fill="#667085" fontFamily={FONT} fontWeight="500">
              {SHORT[d.name] || d.name.slice(0, 9)}
            </text>
            <text x={lp.x} y={lp.y + dy1 + 13} textAnchor={anchor} fontSize="12"   fill={BRAND} fontFamily={FONT_MONO} fontWeight="700">
              {d.score}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ── Audit version pill ────────────────────────────────────────────────
const AuditPill = ({ assessment, index, isSelected, onClick, onDelete }) => {
  const date = new Date(assessment.completedAt || assessment.updatedAt || assessment.createdAt)
    .toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
      <button
        onClick={onClick}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '7px 32px 7px 14px', borderRadius: 999, cursor: 'pointer',
          border: `1.5px solid ${isSelected ? BRAND : '#D7DCE4'}`,
          background: isSelected ? '#F2F6FE' : '#fff',
          color: isSelected ? '#1B4098' : '#2C3646',
          fontSize: 12, fontWeight: isSelected ? 700 : 500,
          fontFamily: FONT, transition: 'all 0.15s', whiteSpace: 'nowrap',
        }}
      >
        {index === 0 && (
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
            background: BRAND, color: '#fff',
            padding: '2px 5px', borderRadius: 6, textTransform: 'uppercase',
          }}>NEU</span>
        )}
        {date}
        <span style={{ color: isSelected ? '#9BB6F2' : '#B6BDC9', fontSize: 10 }}>
          {assessment.title || 'Audit'}
        </span>
      </button>
      <button
        onClick={e => { e.stopPropagation(); onDelete(assessment); }}
        title="Audit löschen"
        style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#B6BDC9', padding: 2, display: 'flex', alignItems: 'center',
          borderRadius: 4, lineHeight: 1,
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#C22B31'}
        onMouseLeave={e => e.currentTarget.style.color = '#B6BDC9'}
      >
        <Trash2 width={11} height={11} />
      </button>
    </div>
  );
};

// ── Delete confirmation modal ───────────────────────────────────────────
// ── Main ───────────────────────────────────────────────────────────────
const DashboardPage = () => {
  const navigate = useNavigate();
  const { activeAuditId, setActiveAuditId } = useActiveAudit();
  const isMobile = useIsMobile();

  const [company,            setCompany]           = useState(null);
  const [assessments,        setAssessments]        = useState([]);
  const [doneAudits,         setDoneAudits]         = useState([]);
  const [postureCache,       setPostureCache]       = useState({});  // id → posture
  const [selectedIdx,        setSelectedIdx]        = useState(0);
  const [loadingPosture,     setLoadingPosture]     = useState(false);
  const [loading,            setLoading]            = useState(true);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showCreateCompany,  setShowCreateCompany]  = useState(false);
  const [deleteTarget,       setDeleteTarget]       = useState(null);
  const [deleting,           setDeleting]           = useState(false);
  const [creatingFollowUp,   setCreatingFollowUp]   = useState(false);
  const [completeTarget,     setCompleteTarget]     = useState(null);
  const [completing,         setCompleting]         = useState(false);
  const [generatingReport,   setGeneratingReport]   = useState(false);
  const [reportError,        setReportError]        = useState(null);
  const [claimable,          setClaimable]          = useState(null);   // abgeschlossenes anonymes Audit zum Übernehmen
  const [claiming,           setClaiming]           = useState(false);
  const [claimNotice,        setClaimNotice]        = useState(null);
  const [hasAccess,          setHasAccess]          = useState(false);   // aktives Abo → Paid-Features (AUD-56)
  const [teaser,             setTeaser]             = useState(null);    // Maßnahmen-Teaser fürs Free-Tier (AUD-57)
  const [upgrading,          setUpgrading]          = useState(false);

  // Zugangs-Gating läuft zentral über SubscriptionGate (App.js): diese Seite wird
  // nur bei aktivem Abo (trialing/active) gerendert. Daher keine eigene Abo-Prüfung
  // mehr (früher Clerks has({ plan }) — nicht mehr gültig seit direkter Stripe-Integration).

  // loadAll liest activeAuditId nur beim Start, um die zuletzt gewählte Auswahl
  // wiederherzustellen — über eine Ref, damit ein Auswahlwechsel (der selbst
  // setActiveAuditId aufruft) kein erneutes Laden des ganzen Dashboards auslöst.
  const activeAuditIdRef = useRef(activeAuditId);
  useEffect(() => { activeAuditIdRef.current = activeAuditId; }, [activeAuditId]);

  const loadAll = useCallback(async () => {
    try {
      const cRes = await companyService.getMyCompany();
      setCompany(cRes.data);
      const aRes = await assessmentService.getAssessmentsByCompany(cRes.data.id);
      setAssessments(aRes.data);

      const done = [...aRes.data]
        .filter(a => a.status === 'completed')
        .sort((a, b) => new Date(b.completedAt || b.updatedAt || b.createdAt) - new Date(a.completedAt || a.updatedAt || a.createdAt));

      setDoneAudits(done);

      if (done.length > 0) {
        // Aktives Audit (Context) übernehmen, falls es unter den vorhandenen
        // Audits existiert; sonst auf das neueste zurückfallen und als aktiv setzen.
        const currentActiveId = activeAuditIdRef.current;
        const idxFromContext = done.findIndex(a => a.id === currentActiveId);
        const initialIdx = idxFromContext >= 0 ? idxFromContext : 0;
        setSelectedIdx(initialIdx);
        if (done[initialIdx].id !== currentActiveId) setActiveAuditId(done[initialIdx].id);

        const p = await fetchPosture(done[initialIdx]);
        setPostureCache({ [done[initialIdx].id]: p });
      } else {
        setActiveAuditId(null);
      }
    } catch (err) {
      if (err.response?.status === 404) setShowCreateCompany(true);
    } finally {
      setLoading(false);
    }
  }, [setActiveAuditId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Abo-Status für das UI-Gating der Paid-Aktionen (Folge-Audit). Das serverseitige
  // Gating (requireFeature) ist die eigentliche Absicherung; das hier steuert nur,
  // ob wir die Aktion ausführen oder zur Pricing-Seite führen (AUD-56).
  useEffect(() => {
    let cancelled = false;
    paymentService.getSubscription()
      .then((res) => { if (!cancelled) setHasAccess(Boolean(res.data?.hasAccess)); })
      .catch(() => { if (!cancelled) setHasAccess(false); });
    return () => { cancelled = true; };
  }, []);

  // Maßnahmen-Teaser (AUD-57): nur für Free-Nutzer und nur für das gerade gewählte,
  // abgeschlossene Audit. Zeigt die aus dem echten Ergebnis abgeleiteten Maßnahmen.
  useEffect(() => {
    const audit = doneAudits[selectedIdx];
    if (!audit || hasAccess) { setTeaser(null); return; }
    let cancelled = false;
    assessmentService.getMeasuresTeaser(audit.id)
      .then((res) => { if (!cancelled) setTeaser(res.data); })
      .catch(() => { if (!cancelled) setTeaser(null); });
    return () => { cancelled = true; };
  }, [doneAudits, selectedIdx, hasAccess]);

  // Upgrade direkt aus dem Teaser: Stripe-Checkout starten, bei Fehler auf die
  // Pricing-Seite ausweichen.
  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const { data } = await paymentService.createCheckoutSession();
      if (data?.url) { window.location.href = data.url; return; }
      navigate('/pricing');
    } catch {
      navigate('/pricing');
    } finally {
      setUpgrading(false);
    }
  };

  // Audit-Claiming (AUD-54): Sobald der Tenant feststeht, einmalig prüfen, ob im
  // Browser noch ein anonymes (httpOnly-Cookie-gebundenes) Ergebnis liegt. Falls
  // ja, den Nutzer per Dialog fragen, ob es dem Konto zugeordnet werden soll —
  // gilt für Signup wie für Login in ein bestehendes Konto (Bestätigungs-Dialog).
  const claimCheckedRef = useRef(false);
  useEffect(() => {
    if (!company || claimCheckedRef.current) return;
    claimCheckedRef.current = true;
    try {
      if (window.sessionStorage?.getItem('audicio_claim_dismissed') === '1') return;
    } catch { /* sessionStorage nicht verfügbar → einfach prüfen */ }
    let cancelled = false;
    (async () => {
      try {
        const res = await anonymousAuditService.getCurrent();
        if (cancelled) return;
        // Nur abgeschlossene Ergebnisse anbieten — ein abgebrochener Check soll
        // nicht als „Ergebnis übernehmen?" auftauchen (bleibt anonym bis TTL-Ablauf).
        if (res.data?.assessment?.status === 'completed') {
          setClaimable(res.data.assessment);
        }
      } catch {
        // 401 = kein/abgelaufener Anon-Cookie → nichts zu übernehmen.
      }
    })();
    return () => { cancelled = true; };
  }, [company]);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const res = await claimService.claimAnonymousAudit();
      setClaimable(null);
      if (res.data?.claimed) {
        setClaimNotice('Ihr anonymes Check-Ergebnis wurde übernommen.');
        await loadAll();
      }
    } catch (err) {
      // z. B. bereits einem anderen Konto zugeordnet (409) — freundlich abweisen.
      setClaimable(null);
      setClaimNotice(err?.response?.data?.error || 'Das Ergebnis konnte nicht übernommen werden.');
    } finally {
      setClaiming(false);
    }
  };

  const dismissClaim = () => {
    try { window.sessionStorage?.setItem('audicio_claim_dismissed', '1'); } catch { /* ignore */ }
    setClaimable(null);
  };

  // Pre-fetch the neighbouring audit for score-diff comparison
  useEffect(() => {
    const neighbour = doneAudits[selectedIdx + 1];
    if (neighbour && !postureCache[neighbour.id]) {
      fetchPosture(neighbour).then(p =>
        setPostureCache(prev => ({ ...prev, [neighbour.id]: p }))
      );
    }
  }, [selectedIdx, doneAudits, postureCache]);

  const handleSelectAudit = async (idx) => {
    setSelectedIdx(idx);
    const audit = doneAudits[idx];
    if (!audit) return;
    setActiveAuditId(audit.id);
    if (postureCache[audit.id]) return;
    setLoadingPosture(true);
    try {
      const p = await fetchPosture(audit);
      setPostureCache(prev => ({ ...prev, [audit.id]: p }));
    } finally {
      setLoadingPosture(false);
    }
  };

  const handleTemplateSelected = async (template) => {
    setShowTemplatePicker(false);
    try {
      const res = await assessmentService.createAssessment({
        companyId: company.id,
        title: template.name,
        description: template.standard || '',
        templateId: template.id,
      });
      navigate(`/assessment/${res.data.id}`);
    } catch (err) { console.error(err); }
  };

  const handleDeleteAudit = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await assessmentService.deleteAssessment(deleteTarget.id);
      setDeleteTarget(null);
      setSelectedIdx(0);
      await loadAll();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateFollowUp = async (auditId) => {
    // Folge-Audit ist ein Paid-Feature (AUD-56). Ohne aktives Abo zur Pricing-Seite
    // führen statt in den 403 zu laufen; das Backend erzwingt es zusätzlich.
    if (!hasAccess) {
      navigate('/pricing');
      return;
    }
    setCreatingFollowUp(true);
    try {
      const res = await assessmentService.createFollowUp(auditId);
      setActiveAuditId(res.data.id);
      navigate(`/assessment/${res.data.id}`);
    } catch (err) {
      console.error(err);
    } finally {
      setCreatingFollowUp(false);
    }
  };

  const handleCompleteAudit = async () => {
    if (!completeTarget) return;
    setCompleting(true);
    try {
      await assessmentService.updateStatus(completeTarget.id, 'completed');
      setCompleteTarget(null);
      await loadAll();
    } catch (err) {
      console.error(err);
    } finally {
      setCompleting(false);
    }
  };

  const handleDownloadReport = async (auditId) => {
    setGeneratingReport(true);
    try {
      const res = await assessmentService.downloadReport(auditId);
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'Ergebnisbericht.pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setReportError('PDF-Report konnte nicht erstellt werden. Bitte erneut versuchen.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const selectedAudit = doneAudits[selectedIdx] || null;
  const posture       = selectedAudit ? postureCache[selectedAudit.id] || null : null;
  const prevAudit     = doneAudits[selectedIdx + 1] || null;
  const prevPosture   = prevAudit ? postureCache[prevAudit.id] || null : null;
  const scoreDiff     = posture && prevPosture ? posture.overallScore - prevPosture.overallScore : null;

  const lastRunDate = selectedAudit
    ? new Date(selectedAudit.completedAt || selectedAudit.updatedAt || selectedAudit.createdAt)
        .toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  // Claim-Dialog + Rückmeldung — in beiden Dashboard-Zweigen (mit/ohne Ergebnis)
  // eingehängt, damit die Nachfrage unabhängig vom Ladezustand des Audits erscheint.
  const claimUI = (
    <>
      <Dialog
        open={!!claimable}
        onClose={() => !claiming && dismissClaim()}
        title="Anonymes Ergebnis übernehmen?"
        description="Sie haben kürzlich einen CyberRisikoCheck ohne Konto durchgeführt. Möchten Sie dieses Ergebnis zu Ihrem Konto hinzufügen? Es erscheint dann dauerhaft in Ihrer Übersicht."
        footer={
          <>
            <Button variant="secondary" size="md" onClick={dismissClaim} disabled={claiming}>Verwerfen</Button>
            <Button size="md" onClick={handleClaim} disabled={claiming}>{claiming ? 'Wird übernommen …' : 'Übernehmen'}</Button>
          </>
        }
      />
      <Toast message={claimNotice} onDismiss={() => setClaimNotice(null)} />
    </>
  );

  // ── Loading ──
  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', color: '#8A93A3', fontSize: 14, fontFamily: FONT }}>
        Lädt…
      </div>
    </Layout>
  );

  // ── Create company ──
  if (showCreateCompany) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <CompanyOnboardingForm
          onCreated={(c) => { setCompany(c); setShowCreateCompany(false); }}
        />
      </div>
    </Layout>
  );

  // ── No completed assessment yet ──
  if (!posture) {
    const inProgress = assessments.filter(a => a.status === 'in_progress');
    return (
      <Layout>
        <div style={{ padding: '36px 40px', fontFamily: FONT }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#101623', letterSpacing: '-0.011em' }}>
                Übersicht
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8A93A3' }}>
                {company?.name} · noch keine abgeschlossenen Audits
              </p>
            </div>
            {company && (
              <Button onClick={() => setShowTemplatePicker(true)} size="md">Neues Audit</Button>
            )}
          </div>

          <Card padding={0}>
            <div style={{ padding: '64px 40px', textAlign: 'center' }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16, background: '#F2F6FE', color: BRAND,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 18px',
              }}>
                <ShieldCheck width={30} height={30} />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#101623' }}>
                {inProgress.length > 0 ? 'Audit in Bearbeitung' : 'Erstes Audit starten'}
              </h2>
              <p style={{ color: '#667085', fontSize: 14, margin: '0 0 24px', lineHeight: 1.7, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
                {inProgress.length > 0
                  ? 'Schließen Sie Ihr Assessment ab, um den vollständigen Sicherheitsstatus einzusehen.'
                  : 'Führen Sie ein IT-Assessment durch, um Ihren Sicherheitsstatus zu visualisieren und offene Befunde zu identifizieren.'}
              </p>
              {company && (
                <OverflowMenu primaryPosition="start">
                  <Button
                    onClick={() => inProgress.length > 0 ? navigate(`/assessment/${inProgress[0].id}`) : setShowTemplatePicker(true)}
                    size="md"
                  >
                    {inProgress.length > 0 ? 'Assessment fortsetzen →' : 'Assessment starten →'}
                  </Button>
                  {inProgress.length > 0 && (
                    <Button variant="secondary" size="md" onClick={() => setCompleteTarget(inProgress[0])}>
                      Audit abschließen
                    </Button>
                  )}
                  {inProgress.length > 0 && (
                    <Button variant="secondary" size="md" disabled title="Bericht ist erst nach Abschluss des Audits verfügbar.">
                      PDF-Report generieren
                    </Button>
                  )}
                  {inProgress.length > 0 && (
                    <Button variant="danger" size="md" onClick={() => setDeleteTarget(inProgress[0])} title="Audit löschen">
                      <Trash2 width={15} height={15} />
                    </Button>
                  )}
                </OverflowMenu>
              )}
            </div>
          </Card>
        </div>
        {showTemplatePicker && <TemplatePicker onSelect={handleTemplateSelected} onCancel={() => setShowTemplatePicker(false)} />}
        {deleteTarget && (
          <DeleteAuditModal
            target={deleteTarget}
            deleting={deleting}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={handleDeleteAudit}
          />
        )}
        <Dialog
          open={!!completeTarget}
          onClose={() => !completing && setCompleteTarget(null)}
          title="Audit abschließen?"
          description="Das Ergebnis (Score, Bereiche, offene Befunde) wird fixiert und ist danach nicht mehr veränderbar. Die Kontrollen stehen anschließend als Maßnahmenliste zur Verfügung."
          footer={
            <>
              <Button variant="secondary" size="md" onClick={() => setCompleteTarget(null)} disabled={completing}>Abbrechen</Button>
              <Button size="md" onClick={handleCompleteAudit} disabled={completing}>{completing ? 'Schließt ab…' : 'Ja, abschließen'}</Button>
            </>
          }
        />
        {claimUI}
      </Layout>
    );
  }

  // ── Security Posture Dashboard ──
  const overall    = getRisk(posture.overallScore);
  const radarData  = posture.categories.slice(0, 9);

  return (
    <Layout>
      <div style={{ padding: '36px 40px 48px', fontFamily: FONT }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#101623', letterSpacing: '-0.011em' }}>
              Übersicht
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8A93A3', fontFamily: FONT_MONO }}>
              {company?.name} · {lastRunDate}
            </p>
          </div>
          <OverflowMenu primaryPosition="end">
            {selectedAudit && (
              <Button variant="danger" size="md" onClick={() => setDeleteTarget(selectedAudit)} leadingIcon={<Trash2 width={13} height={13} />}>
                Löschen
              </Button>
            )}
            {selectedAudit && (
              <Button variant="secondary" size="md" disabled={generatingReport} onClick={() => handleDownloadReport(selectedAudit.id)}>
                {generatingReport ? 'Erstellt…' : 'PDF-Report generieren'}
              </Button>
            )}
            {company && selectedAudit && (
              <Button size="md" disabled={creatingFollowUp} onClick={() => handleCreateFollowUp(selectedAudit.id)}>
                {creatingFollowUp ? 'Erstellt…' : (hasAccess ? 'Folge-Audit starten' : 'Folge-Audit (Upgrade)')}
              </Button>
            )}
          </OverflowMenu>
        </div>

        {/* ── Read-only-Hinweis: abgeschlossene Audits sind unveränderlich ── */}
        {selectedAudit && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
            background: '#F2F6FE', border: '1px solid #CBD9F9', borderRadius: 10,
            padding: '11px 16px', marginBottom: 20, fontSize: 13, color: '#1B4098',
          }}>
            <span>
              Abgeschlossen am {new Date(selectedAudit.completedAt || selectedAudit.updatedAt).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })} – Ergebnis unveränderlich. Für einen neuen Stand ein Folge-Audit starten.
            </span>
            {company && (
              <button
                onClick={() => handleCreateFollowUp(selectedAudit.id)}
                disabled={creatingFollowUp}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1B4098', fontWeight: 600, fontSize: 13, fontFamily: FONT, textDecoration: 'underline', flexShrink: 0 }}
              >
                Folge-Audit starten
              </button>
            )}
          </div>
        )}

        {/* ── Audit version selector ── */}
        {doneAudits.length > 1 && (
          <Card padding={0} style={{ marginBottom: 20 }}>
            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8A93A3', flexShrink: 0 }}>
                Audit-Version
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {doneAudits.map((a, i) => (
                  <AuditPill
                    key={a.id}
                    assessment={a}
                    index={i}
                    isSelected={i === selectedIdx}
                    onClick={() => handleSelectAudit(i)}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
              {loadingPosture && (
                <span style={{ fontSize: 12, color: '#8A93A3', marginLeft: 4 }}>Lädt…</span>
              )}
            </div>
          </Card>
        )}

        {/* ── Top row: Overall + Radar ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '290px 1fr', gap: 16, marginBottom: 16 }}>

          {/* Overall Posture */}
          <Card padding={26}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <ScoreRing value={posture.overallScore} label="Gesamt" size={isMobile ? 110 : 140} stroke={11} tone={overall.tone} />
              <Badge tone={overall.tone} dot>{overall.label}</Badge>
              {scoreDiff !== null && (
                <div style={{ fontSize: 12.5, color: scoreDiff >= 0 ? '#17864B' : '#C22B31', fontFamily: FONT_MONO }}>
                  {scoreDiff >= 0 ? '▲' : '▼'} {Math.abs(scoreDiff)} Pkt. zum vorherigen Audit
                </div>
              )}
              <div style={{ display: 'flex', gap: 24, marginTop: 4, width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#101623', fontFamily: FONT_MONO }}>
                    {posture.totalFindings}
                  </div>
                  <div style={{ fontSize: 12, color: '#8A93A3' }}>offene Befunde</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#101623', fontFamily: FONT_MONO }}>
                    {posture.totalControls}
                  </div>
                  <div style={{ fontSize: 12, color: '#8A93A3' }}>Kontrollen geprüft</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Radar */}
          <Card padding={0}>
            <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#101623' }}>Risiko nach Bereich</div>
                <div style={{ fontSize: 12, color: '#8A93A3' }}>
                  {posture.categories.length} Bereiche · {selectedAudit?.description || selectedAudit?.title || 'DIN SPEC 27076'}
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                <RadarChart data={radarData} />
              </div>
            </div>
          </Card>
        </div>

        {/* ── Category grid ── */}
        <div className="dash-tile-grid">
          {posture.categories.map(cat => (
            <CategoryCard key={cat.name} name={cat.name} score={cat.score} findings={cat.findings} />
          ))}
        </div>

        {/* ── Maßnahmen-Teaser (AUD-57): nur Free-Tier, personalisiert aus dem Audit ── */}
        {!hasAccess && teaser && (
          <MeasuresTeaser teaser={teaser} onUpgrade={handleUpgrade} upgrading={upgrading} />
        )}
      </div>

      {showTemplatePicker && (
        <TemplatePicker onSelect={handleTemplateSelected} onCancel={() => setShowTemplatePicker(false)} />
      )}

      {deleteTarget && (
        <DeleteAuditModal
          target={deleteTarget}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteAudit}
        />
      )}

      <Toast message={reportError} onDismiss={() => setReportError(null)} />
      {claimUI}
    </Layout>
  );
};

export default DashboardPage;
